/**
 * Toast + Undo (docs/03 §2, docs/04 §3). Announced via aria-live; wording
 * matches the action verb; errors that need field anchoring never arrive here
 * (docs/04 §7).
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ToastInput {
  readonly message: string;
  readonly undo?: () => void;
}

interface ActiveToast extends ToastInput {
  readonly id: number;
}

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

export function useToast(): (toast: ToastInput) => void {
  const push = useContext(ToastContext);
  if (push === null) throw new Error('useToast requires <ToastProvider>');
  return push;
}

const TOAST_MS = 5000;

export function ToastProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<readonly ActiveToast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((toast: ToastInput) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { ...toast, id }]);
    setTimeout(() => {
      setToasts((current) => current.filter((t) => t.id !== id));
    }, TOAST_MS);
  }, []);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 pb-[env(safe-area-inset-bottom)]"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="pointer-events-auto flex items-center gap-3 rounded-card bg-brand-900 px-4 py-2.5 text-body text-on-green shadow-lg"
          >
            <span>{toast.message}</span>
            {toast.undo !== undefined ? (
              <button
                type="button"
                onClick={() => {
                  toast.undo?.();
                  setToasts((current) => current.filter((t) => t.id !== toast.id));
                }}
                className="min-h-6 font-semibold text-gold underline"
              >
                Undo
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
